import { parse as yamlParse } from 'yaml';
import { TpTemplateInfo } from '../api/models/assets';

export const TEMPLATE_QUERY_PARAM = 'template';

/**
 * Generates a deterministic synthetic template ID for asset-embedded templates.
 *
 * @param assetId - The UUID or identifier of the asset.
 * @param scopeSuffix - Suffix indicating template scope ('dashboard' or 'entity').
 * @returns Synthetic ID of the form `${assetId}_${scopeSuffix}`.
 */
export const getAssetTemplateId = (assetId: string, scopeSuffix: 'dashboard' | 'entity'): string =>
  `${assetId}_${scopeSuffix}`;

/**
 * Comparator used to order templates by "recency": most recently updated first.
 *
 * Ordering rules:
 * 1. Templates are compared by their `updatedAt` timestamp, descending (most recent first).
 * 2. Templates with a missing/invalid `updatedAt` are treated as the oldest possible value,
 *    so they always sort after templates with a valid timestamp.
 * 3. If both templates have the same (or both missing) `updatedAt`, `id` is used ascending
 *    as a deterministic tie-breaker.
 *
 * @param a - The first template to compare.
 * @param b - The second template to compare.
 * @returns A negative number if `a` should sort before `b`, positive if after, 0 if equal.
 */
export const compareTemplatesByRecency = (a: TpTemplateInfo, b: TpTemplateInfo): number => {
  const getTime = (template: TpTemplateInfo): number => {
    const time = template.updatedAt ? new Date(template.updatedAt).getTime() : NaN;
    return Number.isNaN(time) ? Number.NEGATIVE_INFINITY : time;
  };

  const timeDifference = getTime(b) - getTime(a);
  if (timeDifference !== 0) return timeDifference;

  const idA = a.id ?? '';
  const idB = b.id ?? '';
  return idA < idB ? -1 : idA > idB ? 1 : 0;
};

/**
 * Returns a new, sorted copy of the given templates ordered by recency.
 * See {@link compareTemplatesByRecency} for the exact ordering rules.
 *
 * This is the single, centralized ordering function that should be reused everywhere
 * a list of templates needs to be displayed or reduced to "the most recent one".
 *
 * @param templates - The templates to sort.
 * @returns A new array containing the same templates, sorted by recency. The input array is not mutated.
 */
export const sortTemplatesByRecency = (templates: TpTemplateInfo[]): TpTemplateInfo[] => {
  return [...templates].sort(compareTemplatesByRecency);
};

/**
 * Comparator used to order templates by selection priority:
 * 1. Default templates (`isDefault === true`) sort before non-default templates.
 * 2. Amongst templates with the same default status, order by recency (`updatedAt` descending, `id` ascending tie-breaker).
 *
 * @param a - The first template to compare.
 * @param b - The second template to compare.
 * @returns A negative number if `a` should sort before `b`, positive if after, 0 if equal.
 */
export const compareTemplatesByPriority = (a: TpTemplateInfo, b: TpTemplateInfo): number => {
  const isDefaultA = Boolean(a.isDefault);
  const isDefaultB = Boolean(b.isDefault);

  if (isDefaultA !== isDefaultB) {
    return isDefaultA ? -1 : 1;
  }

  return compareTemplatesByRecency(a, b);
};

/**
 * Returns a new, sorted copy of the given templates ordered by selection priority.
 *
 * Priority rules:
 * 1. `isDefault === true` templates first.
 * 2. More recently updated templates (`updatedAt` descending) first.
 * 3. Tie-breaker on `id` ascending.
 *
 * @param templates - The templates to sort.
 * @returns A new array containing the same templates, sorted by priority. The input array is not mutated.
 */
export const sortTemplatesByPriority = (templates: TpTemplateInfo[]): TpTemplateInfo[] => {
  return [...templates].sort(compareTemplatesByPriority);
};

/**
 * Returns available dashboard templates for a given asset type, filtered by user permissions and sorted by priority.
 *
 * Permission rules:
 * - Regular users (`userIsAdmin === false`): only templates with `isActive === true` are returned.
 * - Organization admins (`userIsAdmin === true`): all templates (both active and inactive) are returned.
 *
 * Ordering:
 * Templates are sorted according to {@link sortTemplatesByPriority}.
 *
 * @param dashboardTemplates - Map of dashboard templates grouped by template type (from Redux state).
 * @param assetType - The asset type (i.e. template type) to fetch templates for.
 * @param userIsAdmin - Whether the current user has organization admin permissions.
 * @returns Array of available templates sorted by priority.
 */
export const getAvailableDashboardTemplatesForAsset = (
  dashboardTemplates: Record<string, TpTemplateInfo[]> | undefined,
  assetType: string,
  assetId: string,
  userIsAdmin: boolean,
): TpTemplateInfo[] => {
  const templates = dashboardTemplates?.[assetType];
  if (!templates || templates.length === 0) return [];

  let eligibleTemplates = userIsAdmin ? templates : templates.filter((template) => template.isActive === true);

  // Filter out asset-specific templates that do not belong to the current asset.
  eligibleTemplates = eligibleTemplates.filter((template) => !template.assetId || template.assetId === assetId);

  return sortTemplatesByPriority(eligibleTemplates);
};

/**
 * Selects the single "most relevant" template out of a list of templates for the same scope/type.
 *
 * Selection rules:
 * 1. Only templates with `isActive === true` are considered. If none are active, `undefined` is
 *    returned - there is intentionally **no fallback** to an inactive/first template.
 * 2. Amongst the active templates, any template with `isDefault === true` takes priority over recency.
 *    - If exactly one active template is marked as default, it is returned.
 *    - If multiple active templates are (incorrectly) marked as default at the same time, a warning
 *      is logged and the most recently updated one (per {@link compareTemplatesByRecency}) is returned
 *      deterministically.
 * 3. If no active template is marked as default, the most recently updated active template is returned.
 *
 * @param templates - The templates to select from (all templates of one scope + template type).
 * @returns The most relevant template, or `undefined` if no active template exists.
 */
export const selectMostRelevantTemplate = (templates: TpTemplateInfo[] | undefined): TpTemplateInfo | undefined => {
  if (!templates || templates.length === 0) return undefined;

  const activeTemplates = templates.filter((template) => template.isActive === true);
  if (activeTemplates.length === 0) return undefined;

  const defaultTemplates = activeTemplates.filter((template) => template.isDefault === true);
  if (defaultTemplates.length > 0) {
    if (defaultTemplates.length > 1) {
      console.warn(
        `Multiple active default templates found for template type "${activeTemplates[0]?.templateType}" ` +
          `(scope "${activeTemplates[0]?.scope}"). Falling back to the most recently updated one.`,
      );
    }
    return sortTemplatesByRecency(defaultTemplates)[0];
  }

  return sortTemplatesByRecency(activeTemplates)[0];
};

/**
 * Resolves the template that should be rendered for a given asset, taking asset-level template
 * overrides into account.
 *
 * Some assets carry their own embedded dashboard/entity template (built from the asset's own
 * `properties.templates.*` in `api/util.ts`), identified by a synthetic id of the form
 * `${assetId}_dashboard` or `${assetId}_entity`. When such an override exists it always takes
 * priority, regardless of its `isActive`/`isDefault` flags (it has none, since it's not persisted
 * as a standalone versioned template). Otherwise, the standard org-level selection rules from
 * {@link selectMostRelevantTemplate} apply.
 *
 * @param templates - All templates for the relevant scope + template type (asset type).
 * @param assetId - The id of the asset the template is being resolved for, if any.
 * @param scopeSuffix - Which asset-embedded override to look for: `'dashboard'` or `'entity'`.
 * @returns The resolved template, or `undefined` if no override exists and no active/default template is found.
 */
export const resolveTemplateForAsset = (
  templates: TpTemplateInfo[] | undefined,
  assetId: string | undefined,
  scopeSuffix: 'dashboard' | 'entity',
): TpTemplateInfo | undefined => {
  if (!templates) return undefined;

  if (assetId) {
    const overrideTemplate = templates.find((template) => template.id === getAssetTemplateId(assetId, scopeSuffix));
    if (overrideTemplate) return overrideTemplate;
  }

  return selectMostRelevantTemplate(templates);
};

interface MergedAssetTypeAccumulator {
  metadata: Record<string, any>;
  properties: Record<string, any>;
  relations: Record<string, any>;
}

/**
 * Performs a "smart" deep merge of every active ontology template into a single combined ontology,
 * where later (more recently updated) ontology templates win on a per-field basis.
 *
 * Merge rules:
 * - Only ontology templates with `isActive !== false` are considered (a missing `isActive` is
 *   treated as active, matching how ontology templates are seeded historically).
 * - Templates are processed in ascending `updatedAt` order (oldest first), so that later templates
 *   overwrite earlier ones - i.e. "last writer wins".
 * - `entityTypes` are merged per entity-type id via a shallow object spread (last writer wins per key).
 * - `assetTypes` are deep-merged per asset-type id:
 *   - Plain metadata fields (everything except `properties`/`relations`) are shallow-merged, last wins.
 *   - `properties` are de-duplicated by their `propertyId` (or by the raw string value, for
 *     shorthand string properties), so a later template can override a specific property without
 *     dropping properties defined by earlier templates.
 *   - `relations` are de-duplicated by their `identifier`, with the same last-writer-wins semantics.
 *
 * @param ontologyTemplates - The raw ontology templates (`state.template.ontologies.ontology`).
 * @returns The combined `{ assetTypes, entityTypes }` ontology definition.
 */
export const mergeOntologyDefinitions = (
  ontologyTemplates: TpTemplateInfo[] | undefined,
): { assetTypes: Record<string, any>; entityTypes: Record<string, any> } => {
  const combinedOntology: { assetTypes: Record<string, any>; entityTypes: Record<string, any> } = {
    assetTypes: {},
    entityTypes: {},
  };

  if (!ontologyTemplates || ontologyTemplates.length === 0) return combinedOntology;

  const activeOntologies = ontologyTemplates
    .filter((template) => template.isActive !== false)
    .sort((a, b) => compareTemplatesByRecency(b, a)); // ascending order: oldest first

  const mergedAssetTypesMap: Record<string, MergedAssetTypeAccumulator> = {};

  activeOntologies.forEach((ontology) => {
    try {
      const parsed = yamlParse(ontology.yamlString) as any;
      if (!parsed) return;

      // Entity Types: Last-Writer-Wins shallow merge
      if (parsed.entityTypes) {
        Object.entries(parsed.entityTypes).forEach(([id, entityDef]: [string, any]) => {
          combinedOntology.entityTypes[id] = {
            ...combinedOntology.entityTypes[id],
            ...entityDef,
          };
        });
      }

      // Asset Types: Deep merge metadata, properties, and relations
      if (parsed.assetTypes) {
        Object.entries(parsed.assetTypes).forEach(([assetId, assetDef]: [string, any]) => {
          if (!mergedAssetTypesMap[assetId]) {
            mergedAssetTypesMap[assetId] = { metadata: {}, properties: {}, relations: {} };
          }

          // Extract metadata vs collections
          const { properties, relations, ...metadata } = assetDef;
          mergedAssetTypesMap[assetId].metadata = {
            ...mergedAssetTypesMap[assetId].metadata,
            ...metadata,
          };

          // Deduplicate properties by id
          (properties || []).forEach((prop: any) => {
            const key = typeof prop === 'string' ? prop : prop?.propertyId;
            if (key) mergedAssetTypesMap[assetId].properties[key] = prop;
          });

          // Deduplicate relations by identifier
          (relations || []).forEach((rel: any) => {
            if (rel?.identifier) {
              mergedAssetTypesMap[assetId].relations[rel.identifier] = rel;
            }
          });
        });
      }
    } catch (error) {
      console.error(`Error parsing ontology YAML for ${ontology.name}:`, error);
    }
  });

  // Reconstruct assetTypes with array formats
  Object.entries(mergedAssetTypesMap).forEach(([assetId, data]) => {
    combinedOntology.assetTypes[assetId] = {
      ...data.metadata,
      properties: Object.values(data.properties),
      relations: Object.values(data.relations),
    };
  });

  return combinedOntology;
};
